import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, CreditCard, Banknote, Bot, GraduationCap, LibraryBig,
  FileText, PenSquare, TrendingUp, Check, CheckCircle2, Sparkles, Info,
  ArrowRight, X, ChevronLeft, ChevronRight, Lock, Crown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const GOLD = "text-[#D4AF37]";

function Bar({ label, value }) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-1.5"><span className="text-[#D4D4D8]">{label}</span><span className="text-[#71717A]">{value}%</span></div>
      <Progress value={value} className="h-2 bg-white/5" />
    </div>
  );
}

function Card({ children, testid }) {
  return <div className="rounded-2xl border border-white/8 bg-[#121214] p-6" data-testid={testid}>{children}</div>;
}

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
      <div className="flex items-center gap-2 mb-3 text-[#71717A]"><Icon className={`w-4 h-4 ${GOLD}`} /><span className="text-[11px] uppercase tracking-wider">{label}</span></div>
      <div className="font-heading text-3xl text-white">{value}</div>
      <div className="text-xs text-[#71717A] mt-1">{sub}</div>
    </div>
  );
}

// ---- Sample module renderers (read-only) ----
const renderDashboard = () => (
  <div className="space-y-5">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Kpi icon={TrendingUp} label="Overall progress" value="62%" sub="Across all modules" />
      <Kpi icon={CreditCard} label="Business credit" value="712" sub="Good" />
      <Kpi icon={Banknote} label="Funding readiness" value="74%" sub="Loan & grant ready" />
      <Kpi icon={GraduationCap} label="Courses completed" value="3/6" sub="Founder training" />
    </div>
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2"><Card><div className="text-[#71717A] text-xs uppercase tracking-wider mb-4">Progress by module</div>
        <div className="space-y-4"><Bar label="Business Formation" value={100} /><Bar label="Business Credit" value={65} /><Bar label="Funding Readiness" value={74} /><Bar label="Documents" value={40} /></div>
      </Card></div>
      <Card><div className="flex items-center gap-2 mb-3"><Sparkles className={`w-4 h-4 ${GOLD}`} /><span className="text-[11px] uppercase tracking-wider text-[#71717A]">AI recommendation</span></div>
        <div className="text-white font-medium mb-1">Add 2 more net-30 tradelines</div>
        <p className="text-xs text-[#A1A1AA]">More reporting tradelines with on-time payments will push your Paydex above 80 and unlock better funding offers.</p>
      </Card>
    </div>
  </div>
);

const renderFormation = () => {
  const tasks = [["Choose your business structure", true], ["Register your business name", true], ["File formation documents", true], ["Get your EIN", true], ["Open a business bank account", false], ["Obtain licenses & permits", false]];
  return (
    <div className="space-y-5">
      <Card><div className="grid sm:grid-cols-3 gap-4 text-sm">
        <div><div className="text-[#71717A] text-xs uppercase mb-1">State</div><div className="text-white">Delaware</div></div>
        <div><div className="text-[#71717A] text-xs uppercase mb-1">Filing fee</div><div className="text-white">$90</div></div>
        <div><div className="text-[#71717A] text-xs uppercase mb-1">Processing</div><div className="text-white">1–3 business days</div></div>
      </div></Card>
      <Card testid="demo-formation-tasks"><div className="text-[#71717A] text-xs uppercase tracking-wider mb-4">Formation checklist · 4 of 6 complete</div>
        <div className="space-y-2">{tasks.map(([t, done]) => (
          <div key={t} className={`flex items-center gap-3 rounded-xl border p-4 ${done ? "border-[#D4AF37]/25 bg-[#151310]" : "border-white/8 bg-[#0d0d0f]"}`}>
            {done ? <CheckCircle2 className={`w-5 h-5 ${GOLD}`} /> : <div className="w-5 h-5 rounded-full border border-[#52525B]" />}
            <span className={`text-sm ${done ? "text-white" : "text-[#D4D4D8]"}`}>{t}</span>
          </div>))}
        </div>
      </Card>
    </div>
  );
};

const renderCredit = () => (
  <div className="space-y-5">
    <div className="grid sm:grid-cols-3 gap-4">
      <Kpi icon={CreditCard} label="Score" value="712" sub="Good" />
      <Kpi icon={Check} label="Steps done" value="6/8" sub="Credit builders" />
      <Kpi icon={TrendingUp} label="Tradelines" value="4" sub="Reporting on-time" />
    </div>
    <Card><div className="text-[#71717A] text-xs uppercase tracking-wider mb-4">Active tradelines</div>
      <div className="space-y-2">{[["Uline", "Net 30", "Approved"], ["Grainger", "Net 30", "Approved"], ["Quill", "Net 30", "Applied"]].map(([v, term, st]) => (
        <div key={v} className="flex items-center justify-between rounded-xl border border-white/8 bg-[#0d0d0f] p-4">
          <div><div className="text-white text-sm">{v}</div><div className="text-xs text-[#71717A]">{term}</div></div>
          <span className={`text-xs px-2.5 py-1 rounded-full ${st === "Approved" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{st}</span>
        </div>))}
      </div>
    </Card>
  </div>
);

const renderFunding = () => (
  <div className="space-y-5">
    <Card><div className="flex items-center justify-between mb-4"><span className="text-[#71717A] text-xs uppercase tracking-wider">Funding readiness</span><span className={`font-heading text-3xl ${GOLD}`}>74%</span></div>
      <div className="space-y-2 text-sm">{[["Legal entity formed", true], ["Business credit established", true], ["Financial documents uploaded", true], ["Business plan ready", false]].map(([t, d]) => (
        <div key={t} className="flex items-center gap-2 text-[#D4D4D8]">{d ? <Check className={`w-4 h-4 ${GOLD}`} /> : <div className="w-4 h-4 rounded-full border border-[#52525B]" />}{t}</div>))}
      </div>
    </Card>
    <Card><div className="text-[#71717A] text-xs uppercase tracking-wider mb-4">Matched opportunities</div>
      <div className="space-y-2">{[["SBA 7(a) Loan", "Up to $5M", 92], ["Business Line of Credit", "$10K–$250K", 80], ["Revenue-Based Financing", "$25K–$1M", 70]].map(([n, amt, m]) => (
        <div key={n} className="flex items-center justify-between rounded-xl border border-white/8 bg-[#0d0d0f] p-4">
          <div><div className="text-white text-sm">{n}</div><div className="text-xs text-[#71717A]">{amt}</div></div>
          <span className={`text-xs ${GOLD}`}>{m}% match</span>
        </div>))}
      </div>
    </Card>
  </div>
);

const renderAI = () => (
  <Card testid="demo-ai">
    <div className="flex items-center gap-2 mb-4"><Bot className={`w-5 h-5 ${GOLD}`} /><span className="text-white font-medium">Fairview AI Advisor</span></div>
    <div className="space-y-3">
      <div className="flex justify-end"><div className="bg-[#D4AF37]/15 text-[#E4E4E7] text-sm rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[80%]">What should I focus on to get funding-ready?</div></div>
      <div className="flex justify-start"><div className="bg-[#0d0d0f] border border-white/8 text-[#D4D4D8] text-sm rounded-2xl rounded-bl-sm px-4 py-3 max-w-[85%]">
        You're 74% funding-ready — great progress! Your top three moves: <span className="text-white">(1)</span> finish your business plan (last checklist item), <span className="text-white">(2)</span> add 2 more net-30 tradelines to push your Paydex above 80, and <span className="text-white">(3)</span> upload 6 months of bank statements. Do these and you'll qualify for the SBA 7(a) match at 92%.
      </div></div>
    </div>
    <div className="mt-4 rounded-xl border border-white/8 bg-[#0d0d0f] px-4 py-3 text-sm text-[#52525B] flex items-center gap-2"><Lock className="w-4 h-4" /> Sign up to chat with your own context-aware AI advisor.</div>
  </Card>
);

const renderEducation = () => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
    {[["Business Foundations 101", "Formation", 100], ["Business Credit Mastery", "Credit", 75], ["Getting Funding Ready", "Funding", 50], ["Compliance & Legal Essentials", "Compliance", 0], ["Growth & Marketing Playbook", "Marketing", 0], ["Operations & Systems", "Operations", 0]].map(([t, cat, p]) => (
      <div key={t} className="rounded-2xl border border-white/8 bg-[#121214] p-6">
        <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-4"><GraduationCap className={`w-5 h-5 ${GOLD}`} /></div>
        <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">{cat}</div>
        <h3 className="font-heading text-lg text-white mb-3">{t}</h3>
        <div className="flex justify-between text-xs mb-1.5"><span className="text-[#A1A1AA]">4 lessons</span><span className={GOLD}>{p}%</span></div>
        <Progress value={p} className="h-1.5 bg-white/5" />
      </div>))}
  </div>
);

const renderResources = () => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {[["LLC Formation Checklist", "Formation", "Checklist"], ["Net-30 Vendor List", "Credit", "Template"], ["Lender-Ready Loan Package", "Funding", "Template"], ["Mutual NDA Template", "Legal", "Template"], ["Business Plan Template", "Funding", "Template"], ["90-Day Marketing Plan", "Growth", "Guide"]].map(([t, cat, type]) => (
      <div key={t} className="rounded-2xl border border-white/8 bg-[#121214] p-5">
        <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-4"><LibraryBig className={`w-5 h-5 ${GOLD}`} /></div>
        <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">{cat} · {type}</div>
        <div className="text-white font-medium">{t}</div>
      </div>))}
  </div>
);

const renderDocuments = () => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {[["Articles of Organization.pdf", "Formation"], ["EIN Confirmation.pdf", "Tax"], ["Operating Agreement.pdf", "Legal"], ["Bank Statement - Jan.pdf", "Financial"]].map(([f, cat]) => (
      <div key={f} className="rounded-2xl border border-white/8 bg-[#121214] p-5">
        <div className="flex items-start justify-between mb-4"><div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center"><FileText className={`w-5 h-5 ${GOLD}`} /></div>
          <span className="text-[10px] text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-2 py-0.5">{cat}</span></div>
        <div className="text-white text-sm font-medium truncate">{f}</div><div className="text-xs text-[#71717A] mt-1">Secured · encrypted</div>
      </div>))}
  </div>
);

const CONTRACT_STATUS_STYLE = {
  Signed: "bg-emerald-500/15 text-emerald-400",
  Sent: "bg-amber-500/15 text-amber-400",
  Draft: "bg-white/10 text-[#A1A1AA]",
};

const renderContracts = () => (
  <div className="space-y-2">
    {[["Mutual NDA — Acme Partners", "Signed"], ["Service Agreement — BrightCo", "Sent"], ["Contractor Agreement — J. Rivera", "Draft"]].map(([t, st]) => (
      <div key={t} className="flex items-center justify-between rounded-xl border border-white/8 bg-[#121214] p-4">
        <div className="flex items-center gap-3"><PenSquare className={`w-5 h-5 ${GOLD}`} /><span className="text-white text-sm">{t}</span></div>
        <span className={`text-xs px-2.5 py-1 rounded-full ${CONTRACT_STATUS_STYLE[st]}`}>{st}</span>
      </div>))}
  </div>
);

const renderProgress = () => (
  <Card><div className="flex items-center justify-between mb-5"><div><div className="text-[#71717A] text-xs uppercase tracking-wider mb-1">Overall completion</div><div className="font-heading text-4xl text-white">62%</div></div><TrendingUp className={`w-8 h-8 ${GOLD}`} /></div>
    <div className="space-y-4"><Bar label="Business Formation" value={100} /><Bar label="Business Credit" value={65} /><Bar label="Funding Readiness" value={74} /><Bar label="Documents" value={40} /></div>
  </Card>
);

export const MODULES = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, tagline: "Your entire business at a glance — progress, credit, funding readiness and AI recommendations.", render: renderDashboard },
  { id: "formation", label: "Business Formation", icon: Building2, tagline: "A guided, state-by-state checklist to form your entity the right way.", render: renderFormation },
  { id: "credit", label: "Business Credit", icon: CreditCard, tagline: "Build business credit separate from your personal score with net-30 tradelines.", render: renderCredit },
  { id: "funding", label: "Funding Center", icon: Banknote, tagline: "See your funding-readiness score and opportunities matched to your business.", render: renderFunding },
  { id: "ai", label: "AI Business Advisor", icon: Bot, tagline: "A context-aware advisor that reads your progress and tells you exactly what to do next.", render: renderAI },
  { id: "education", label: "Education & Training", icon: GraduationCap, tagline: "Founder courses across formation, credit, funding, compliance, marketing and operations.", render: renderEducation },
  { id: "resources", label: "Resource Center", icon: LibraryBig, tagline: "A searchable library of templates, guides and checklists you can download.", render: renderResources },
  { id: "documents", label: "Document Library", icon: FileText, tagline: "Bank-grade secure storage for every important business document.", render: renderDocuments },
  { id: "contracts", label: "Contracts & Agreements", icon: PenSquare, tagline: "Generate agreements from templates and sign them electronically.", render: renderContracts },
  { id: "progress", label: "Progress Tracker", icon: TrendingUp, tagline: "Track completion across every module so you always know your next move.", render: renderProgress },
];

export default function Demo() {
  const navigate = useNavigate();
  const [active, setActive] = useState("dashboard");
  const [tourStep, setTourStep] = useState(0);
  const [tourOn, setTourOn] = useState(true);
  const [showEnd, setShowEnd] = useState(false);

  const startTrial = () => navigate("/register?plan=growth");
  const comparePlans = () => navigate("/#pricing");
  const current = MODULES.find((m) => m.id === active);

  const goToStep = (i) => {
    if (i >= MODULES.length) { setTourOn(false); setShowEnd(true); return; }
    if (i < 0) return;
    setTourStep(i); setActive(MODULES[i].id);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 fv-glass border-b border-white/8">
        <div className="px-3 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex items-center gap-2 shrink-0">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg fv-gold-gradient flex items-center justify-center"><span className="font-heading text-[#0A0A0B] text-lg sm:text-xl font-bold">F</span></div>
              <div className="leading-tight hidden sm:block"><div className="font-heading text-lg tracking-wide">Fairview</div><div className="text-[9px] tracking-[0.25em] uppercase text-[#D4AF37]">Systems</div></div>
            </div>
            <span className="text-[10px] sm:text-xs px-2 sm:px-2.5 py-1 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#D4AF37] font-medium whitespace-nowrap truncate" data-testid="demo-mode-badge">Demo<span className="hidden sm:inline"> Mode — Sample Data Only</span></span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Button variant="ghost" onClick={() => navigate("/demo/video")} data-testid="demo-watch-tour"
              className="text-[#A1A1AA] hover:text-white hover:bg-white/5 hidden sm:inline-flex">Watch Demo Video</Button>
            <Button variant="ghost" onClick={comparePlans} data-testid="demo-compare-plans"
              className="text-[#A1A1AA] hover:text-white hover:bg-white/5 hidden sm:inline-flex">Compare Plans</Button>
            <Button onClick={startTrial} data-testid="demo-start-trial"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-3 sm:px-5 h-9 text-xs sm:text-sm whitespace-nowrap">
              <span className="sm:hidden">Free Trial</span><span className="hidden sm:inline">Start Your Free 7-Day Trial</span></Button>
            <button onClick={() => navigate("/")} data-testid="demo-exit" className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg hover:bg-white/5 flex items-center justify-center text-[#A1A1AA] hover:text-white shrink-0" title="Exit demo" aria-label="Exit demo"><X className="w-5 h-5" /></button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-white/8 bg-[#0d0d0f] p-3 gap-0.5">
          <div className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-[#71717A]">Explore modules</div>
          {MODULES.map((m, i) => {
            const Icon = m.icon;
            return (
              <button key={m.id} onClick={() => { setActive(m.id); setTourStep(i); }} data-testid={`demo-nav-${m.id}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${active === m.id ? "bg-[#D4AF37]/12 text-white border border-[#D4AF37]/30" : "text-[#A1A1AA] hover:text-white hover:bg-white/5 border border-transparent"}`}>
                <Icon className="w-[18px] h-[18px] shrink-0" /><span className="truncate">{m.label}</span>
              </button>
            );
          })}
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 pb-28">
          {/* Mobile module selector */}
          <div className="md:hidden mb-4 flex gap-2 overflow-x-auto pb-2">
            {MODULES.map((m) => (
              <button key={m.id} onClick={() => setActive(m.id)} data-testid={`demo-tab-${m.id}`}
                className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap ${active === m.id ? "fv-gold-gradient text-[#0A0A0B] font-medium" : "bg-white/5 text-[#A1A1AA]"}`}>{m.label}</button>
            ))}
          </div>

          <div className="mb-6">
            <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-2">Interactive Demo</div>
            <h1 className="font-heading text-3xl sm:text-4xl text-white tracking-tight">{current.label}</h1>
            <p className="text-[#A1A1AA] mt-2 max-w-2xl flex items-start gap-2"><Info className={`w-4 h-4 mt-0.5 shrink-0 ${GOLD}`} />{current.tagline}</p>
          </div>

          <div onClickCapture={(e) => {
            // Read-only guard: clicks inside interactive-looking elements show a demo notice.
            if (e.target.closest("button, a, input, [role='button']")) {
              toast.info("Demo mode — sample data only. Start your free trial to interact for real.");
            }
          }} data-testid="demo-module-content">
            {current.render()}
          </div>
        </main>
      </div>

      {/* Guided tour tooltip */}
      {tourOn && (
        <div className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-md max-h-[70vh] flex flex-col rounded-2xl border border-[#D4AF37]/30 bg-[#151310] shadow-2xl overflow-hidden" role="dialog" aria-label="Guided walkthrough" data-testid="demo-tour">
          <div className="flex items-center justify-between px-4 sm:px-5 pt-4 pb-2 shrink-0">
            <span className="text-[#D4AF37] text-[10px] uppercase tracking-[0.2em]">Guided walkthrough · {tourStep + 1}/{MODULES.length}</span>
            <button onClick={() => setTourOn(false)} data-testid="demo-tour-skip" className="text-[#71717A] hover:text-white text-xs py-1">Skip walkthrough</button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 sm:px-5 min-h-0">
            <div className="text-white font-heading text-base sm:text-lg mb-1">{current.label}</div>
            <p className="text-[13px] sm:text-sm text-[#A1A1AA]">{current.tagline}</p>
          </div>
          <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3 border-t border-white/8 bg-[#151310] shrink-0">
            <button onClick={() => goToStep(tourStep - 1)} disabled={tourStep === 0} data-testid="demo-tour-back"
              className="flex items-center gap-1 text-sm text-[#A1A1AA] hover:text-white disabled:opacity-30 py-1"><ChevronLeft className="w-4 h-4" /> Back</button>
            <Button onClick={() => goToStep(tourStep + 1)} data-testid="demo-tour-next"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-5 h-9">
              {tourStep === MODULES.length - 1 ? "Finish walkthrough" : "Next"} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Persistent bottom CTA bar */}
      {!tourOn && !showEnd && (
        <div className="fixed bottom-0 inset-x-0 z-40 fv-glass border-t border-[#D4AF37]/20 px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <span className="text-sm text-[#A1A1AA] hidden sm:block">Ready to build your business for real?</span>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="ghost" onClick={comparePlans} className="text-[#A1A1AA] hover:text-white hover:bg-white/5">Compare Plans</Button>
            <Button onClick={startTrial} data-testid="demo-bottom-trial" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-5">Start Your Free 7-Day Trial <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </div>
        </div>
      )}

      {/* End-of-demo CTA modal */}
      {showEnd && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="demo-end-cta">
          <div className="w-full max-w-md rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#151310] to-[#121214] p-8 text-center">
            <div className="w-14 h-14 rounded-2xl fv-gold-gradient mx-auto flex items-center justify-center mb-5"><Crown className="w-7 h-7 text-[#0A0A0B]" /></div>
            <h2 className="font-heading text-3xl text-white mb-2">You've seen what's possible</h2>
            <p className="text-[#A1A1AA] mb-6">Start your free 7-day trial and build it for real — no credit card required. Your data, documents, and progress are yours to keep.</p>
            <div className="space-y-3">
              <Button onClick={startTrial} data-testid="demo-end-trial" className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-12 text-base">Start Your Free 7-Day Trial</Button>
              <Button variant="outline" onClick={comparePlans} data-testid="demo-end-compare" className="w-full border-white/15 bg-transparent text-white hover:bg-white/5 rounded-full h-12">Compare Plans</Button>
              <button onClick={() => setShowEnd(false)} className="text-sm text-[#71717A] hover:text-white pt-1">Keep exploring the demo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
